import { useState, useCallback } from 'react';

import ItemListContainer from '../../global/ItemListContainer/ItemListContainer';
import LoadingBar from '../../global/LoadingBar';
import GiftCardsPreviewList from '../GiftCards/GiftCardsPreviewList';
import ImpactMetrics from '../../global/ImpactMetrics';
import { ROUTE_MY_ACCOUNT } from '../../config/routes';
import { TAKE_BACK_BAG_COLLECTION_URL } from '../../config/externalURL';
import ConfigIcon from '../../images/icon-config.svg';
import ChevronRightIcon from '../../images/chevron-right.svg';
import RecycleIcon from '../../images/icon-recycle-grey.svg';
import MembershipPerks from './MembershipPerks';
import MembershipOrderTbbContainer from '../MembershipOrderTbb/MembershipOrderTbbContainer';
import { MEMBERSHIP_ORDER_TBB_STEPS } from '../MembershipOrderTbb/MembershipOrderTbbContext';
import CollapsibleOrderTbb from '../../global/CollapsibleOrderTbb/CollapsibleOrderTbb';
import RewardsV2Shelf from '../../global/RewardsV2Shelf';
import { REWARD_PREVIEW_TYPES } from '../../config/rewards';
import { insertTCIcon } from '../../utils/tbbRegistration';
import useSubscriptionStatus from '../../../../hooks/useSubscriptionStatus';
import MembershipHeaderWithStatus from '../ManageMembership/MembershipHeaderWithStatus';
import { isMobile, SIZE_XS, SIZE_SM } from '../../../../hooks/useBreakPoint';
import { SLIDER_TRACK_PADDING } from '../../config/config';
import UnlimitedLogoSVG from '../../global/SvgComponents/UnlimitedLogo_svg';
import './MembershipDashboard.scss';

const settingsItems = [
  {
    route: ROUTE_MY_ACCOUNT,
    icon: ConfigIcon,
    title: 'Settings & Details',
  },
];

const tbbItems = [
  {
    route: TAKE_BACK_BAG_COLLECTION_URL,
    icon: RecycleIcon,
    title: 'Give Take Back',
    external: true,
  },
];

const MembershipDashboard = () => {
  const {
    subscriptionStatus,
    isLoading,
    hasError,
    getMembershipSubscriptionData,
  } = useSubscriptionStatus();
  const isMobileSize = isMobile([SIZE_XS, SIZE_SM]);

  const [isConfirmationStep, setIsConfirmationStep] = useState(false);

  const handleStepChange = useCallback((step) => {
    setIsConfirmationStep(step === MEMBERSHIP_ORDER_TBB_STEPS.ORDER_CONFIRMATION);
  }, []);

  const renderContent = () => {
    if (isLoading) {
      return <LoadingBar />;
    }

    if (hasError) {
      return (
        <div className="MembershipDashboard__error">
          <div className="MembershipDashboard__error--message">
            Unable to load membership information
          </div>
          <button
            type="button"
            className="MembershipDashboard__error--retry"
            onClick={getMembershipSubscriptionData}
          >
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div className="MembershipDashboard__container">
        <div className="MembershipDashboard__container--orderTbb">
          {!isConfirmationStep && (
            <div className="MembershipDashboard__container--orderTbb-title">
              Order Bags & Boxes
            </div>
          )}
          {isMobileSize ? (
            <CollapsibleOrderTbb
              className="MembershipDashboard__container--orderTbb-collapsible"
              isConfirmationStep={isConfirmationStep}
              onStepChange={handleStepChange}
            />
          ) : (
            <div className="MembershipDashboard__container--orderTbb-fixed">
              {!isConfirmationStep && (
                <div className="MembershipDashboard__container--orderTbb-fixed-header">
                  <UnlimitedLogoSVG width={257} height={56} />
                </div>
              )}
              <MembershipOrderTbbContainer
                hideOrderCta={false}
                orderConfirmationShowCta
                onStepChange={handleStepChange}
                orderConfirmationClassName="MembershipDashboard__container--orderTbb-fixed-confirmation"
              />
            </div>
          )}
        </div>

        <div className="MembershipDashboard__container--perks">
          <MembershipPerks
            title="Membership Perks"
            refetchSavedMoney={isConfirmationStep}
          />
        </div>

        <RewardsV2Shelf
          title={(
            <div className="MembershipDashboard__promo-section-title">
              {insertTCIcon('Save & Earn %TC%', 24)}
            </div>
          )}
          type={REWARD_PREVIEW_TYPES.DEFAULT}
          defaultSlidesPerPage={isMobileSize ? 2 : 4}
          itemCardBackgroundColor="var(--trashie-ui-crisp-white)"
          advancedMode
          fullWidth={!isMobileSize}
          mobileGap="0.5rem"
          trackPadding={SLIDER_TRACK_PADDING.MEDIUM}
        />

        <RewardsV2Shelf
          title={(
            <div className="MembershipDashboard__promo-section-title">
              {insertTCIcon('Earn %TC% with Trending Deals', 24)}
            </div>
          )}
          type={REWARD_PREVIEW_TYPES.HOT}
          defaultSlidesPerPage={isMobileSize ? 2 : 4}
          itemCardBackgroundColor="var(--trashie-ui-crisp-white)"
          advancedMode
          fullWidth={!isMobileSize}
          mobileGap="0.5rem"
          trackPadding={SLIDER_TRACK_PADDING.MEDIUM}
        />
        <ImpactMetrics className="MembershipDashboard__container--impact-metrics" />
        <GiftCardsPreviewList
          title={insertTCIcon('Redeem %TC% for Gift Cards', 24)}
          className="MembershipDashboard__container--gift-cards"
        />

        <ItemListContainer
          listTitle="Buy for Someone Else"
          items={tbbItems}
          exitIcon={ChevronRightIcon}
          className="MembershipDashboard__container--item-list-container"
        />
        <ItemListContainer
          listTitle={<MembershipHeaderWithStatus subscriptionStatus={subscriptionStatus} titleClassName="MembershipDashboard__header-title" />}
          items={settingsItems}
          exitIcon={ChevronRightIcon}
          className="MembershipDashboard__container--item-list-container"
        />
      </div>
    );
  };

  return (
    <div className="MembershipDashboard">
      {renderContent()}
    </div>
  );
};

export default MembershipDashboard;
